import { APIURL, apiGet, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, toggleWidgetTheme, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { InsiderAcquisitionDispositionConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface InsiderPieChartItem {
    name: string;
    value: number;
    percentage: number;
    amount: number;
    count: number;
    color: string;
}

interface InsiderPieChartApiData {
    labels: string[];
    values: number[];
    percentages: number[];
    counts: number[];
    colors: string[];
}

interface InsiderOverallDistribution {
    total_acquisitions: number;
    total_dispositions: number;
    net_position: number;
    acquisition_percentage: number;
    disposition_percentage: number;
    acquisition_count: number;
    disposition_count: number;
    total_transactions: number;
    total_symbols: number;
}

interface InsiderSymbolBreakdownItem {
    symbol: string;
    acquisitions: number;
    dispositions: number;
    net_position: number;
    acquisition_count: number;
    disposition_count: number;
    total_value: number;
    acquisition_percentage: number;
    disposition_percentage: number;
}

interface InsiderAcquisitionDispositionApiData {
    parameters: {
        symbol: string | null;
        days_back: number;
        min_transaction_value: number;
        show_top_n: number;
        cutoff_date: string;
        query_date: string;
    };
    overall_distribution: InsiderOverallDistribution;
    pie_chart_data: InsiderPieChartApiData;
    symbol_breakdown: InsiderSymbolBreakdownItem[];
}

export class TraderverseMarketInsiderAcquisitionDispositionDistribution {
    private container: HTMLElement;
    private config: InsiderAcquisitionDispositionConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: InsiderPieChartItem[] = [];
    private apiData: InsiderAcquisitionDispositionApiData | null = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private filters = {
        symbol: '' as string | undefined,
        daysBack: 90 as number | '',
        minTransactionValue: 500000 as number | '',
        showTopN: 10 as number | ''
    };

    constructor(config: InsiderAcquisitionDispositionConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<InsiderAcquisitionDispositionConfig> {
        return {
      headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true,
            symbol: 'AAPL',
            daysBack: 90,
            minTransactionValue: 500000,
            showTopN: 10
        };
    }

    private applyConfigFilters(): void {
        if (this.config.symbol) this.filters.symbol = this.config.symbol;
        if (typeof this.config.daysBack === 'number') this.filters.daysBack = this.config.daysBack;
        if (typeof this.config.minTransactionValue === 'number') this.filters.minTransactionValue = this.config.minTransactionValue;
        if (typeof this.config.showTopN === 'number') this.filters.showTopN = this.config.showTopN;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} Insider Acquisition & Disposition Distribution</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                    <div class="item">
                        <button class="filters-toggle" type="button">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>
                    <i class="pi pi-ellipsis-v"></i>
                </div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    ${this.config.hideSymbol ? '' : `<div class="item dropStyle01">
                        <div class="trader-sm-card" id="selected-symbol">
                            ${this.config.symbol
                                ? getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol, type: 'stock', name: '' })
                                : '<span>All Symbols</span>'}
                        </div>
                        <span id="symbol-dropdown-placeholder"></span>
                    </div>`}
                    <span></span>
                    <div class="filtersBox sideBar hidden">
                        <div class="item">
                            <label>Days Back</label>
                            <input type="number" class="filter-days-back" min="1" step="1" placeholder="e.g. 90" value="${this.filters.daysBack}" />
                        </div>
                        <div class="item">
                            <label>Min Transaction Value</label>
                            <input type="number" class="filter-min-transaction-value" min="0" step="1" placeholder="e.g. 500000" value="${this.filters.minTransactionValue}" />
                        </div>
                        <div class="item">
                            <label>Show Top N</label>
                            <input type="number" class="filter-show-top-n" min="1" step="1" placeholder="e.g. 10" value="${this.filters.showTopN}" />
                        </div>
                    </div>
                </div>
                <div class="chartDv cryptoCompany" id="portfolioAssetTypeContainer"></div>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filtersToggle && filtersBox) {
            filtersToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
                placeholder.replaceWith(dropdown);
            }
        }

        const updateNumericFiltersAndFetch = () => {
            this.filters.daysBack = this.readNumberInput('.filter-days-back');
            this.filters.minTransactionValue = this.readNumberInput('.filter-min-transaction-value');
            this.filters.showTopN = this.readNumberInput('.filter-show-top-n');
            this.setupChart();
        };

        [
            '.filter-days-back',
            '.filter-min-transaction-value',
            '.filter-show-top-n'
        ].forEach((selector) => {
            const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
            input?.addEventListener('change', updateNumericFiltersAndFetch);
        });
    }

    private readNumberInput(selector: string): number | '' {
        const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
        if (!input || input.value.trim() === '') return '';
        const value = parseFloat(input.value);
        return Number.isNaN(value) ? '' : value;
    }

    private onSelectChange(stock: any): void {
        this.changeSymbol(stock.symbol, stock);
    }

    private buildQueryParams(): Record<string, string | number> {
        const query: Record<string, string | number> = {};
        if (this.filters.symbol) query.symbol = this.filters.symbol;
        if (this.filters.daysBack !== '') query.daysBack = this.filters.daysBack;
        if (this.filters.minTransactionValue !== '') query.minTransactionValue = this.filters.minTransactionValue;
        if (this.filters.showTopN !== '') query.showTopN = this.filters.showTopN;
        return query;
    }

    private mapApiData(data: InsiderAcquisitionDispositionApiData): InsiderPieChartItem[] {
        const pieChartData = data.pie_chart_data;
        if (!pieChartData?.labels?.length) return [];

        return pieChartData.labels.map((label, index) => {
            const amount = pieChartData.values[index] ?? 0;
            const percentage = pieChartData.percentages[index] ?? 0;
            const count = pieChartData.counts[index] ?? 0;
            return {
                name: label,
                value: amount > 0 ? amount : percentage,
                percentage,
                amount,
                count,
                color: pieChartData.colors[index] ?? (label === 'Acquisitions' ? '#10b981' : '#ef4444')
            };
        });
    }

    private hasChartData(data: InsiderAcquisitionDispositionApiData | null): boolean {
        if (!data) return false;
        const overall = data.overall_distribution;
        if (overall?.total_transactions > 0) return true;
        return (data.pie_chart_data?.values ?? []).some((value) => value > 0);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            script.async = true;
            script.onload = () => resolve(true);
            this.shadowRoot.appendChild(script);
        });
    }

    private async setupChart(symbol?: string): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const container = this.shadowRoot.querySelector('#portfolioAssetTypeContainer');
        if (widget) widget.classList.add('loading');

        if (symbol) {
            this.filters.symbol = symbol;
            this.config.symbol = symbol;
        }

        try {
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/equity-market/insider-acquisition-disposition-distribution`,
                query: this.buildQueryParams(),
                headers: { }
            });
            if (!res.ok) throw new Error('Failed to fetch insider acquisition disposition data');

            const json = await res.json();
            const responseData = json?.data as InsiderAcquisitionDispositionApiData | undefined;
            this.apiData = responseData ?? null;
            if (this.hasChartData(this.apiData)) {
    container?.setAttribute('data-testing-id', 'portfolio-asset-type');
} else {
    container?.removeAttribute('data-testing-id');
}

            if (!this.hasChartData(this.apiData)) {
                this.currentData = [];
                if (container) container.innerHTML = '<div class="noData"><p>No data available</p></div>';
                if (this.currentChart) {
                    this.currentChart.dispose();
                    this.currentChart = null;
                }
            } else {
                this.currentData = this.mapApiData(this.apiData!);
                this.renderPortfolioChart();
            }

            this.emit('dataUpdate', this.apiData);
        } catch (err) {
            console.error('Failed to fetch/setup chart data:', err);
            this.currentData = [];
            this.apiData = null;
            if (container) container.innerHTML = '<div class="noData"><p>Failed to load data</p></div>';
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData?.length || !(window as any).echarts) {
            return;
        }
        this.renderPortfolioChart();
    }

    private renderPortfolioChart(): void {
        const container = this.shadowRoot.querySelector('#portfolioAssetTypeContainer');
        if (!container) return;

        container.innerHTML = `<div id="portfolioChart" class="chart coverDv"></div>`;
        this.drawChart();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#portfolioChart') as HTMLElement;
        if (!container || !this.currentData?.length) return;

        if (this.currentChart) {
            this.currentChart.dispose();
        }

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                backgroundColor: '#0E0C19',
                borderColor: '#1E1B3A',
                borderWidth: 1,
                padding: [10, 14],
                textStyle: {
                    color: '#FFFFFF',
                    fontFamily: 'Inter',
                    fontSize: 12
                },
                formatter: (p: any) => `
              <div>
                <div style="display:flex;align-items:center;font-weight:600;margin-bottom:6px">
                  <span style="
                    width:8px;
                    height:8px;
                    border-radius:50%;
                    background:${p.color};
                    margin-right:6px
                  "></span>
                  ${p.name} &nbsp; ${Number(p.data.percentage).toFixed(1)}%
                </div>
                <div>$${Number(p.data.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div>${p.data.count} transaction${p.data.count === 1 ? '' : 's'}</div>
              </div>
            `
            },
            series: [
                {
                    type: 'pie',
                    radius: ['42%', '62%'],
                    center: ['50%', '48%'],
                    startAngle: 90,
                    minAngle: 10,
                    padAngle: 6,
                    avoidLabelOverlap: true,
                    itemStyle: {
                        borderRadius: 14,
                        borderColor: bgColor,
                        borderWidth: 8
                    },
                    label: {
                        show: true,
                        position: 'outside',
                        formatter: (p: any) =>
                            `${p.name}\n${Number(p.data.percentage).toFixed(1)}%`,
                        color: textColor,
                        fontFamily: 'Inter',
                        fontSize: fontSize,
                        lineHeight: 16
                    },
                    labelLine: {
                        show: true,
                        length: 10,
                        length2: 8,
                        lineStyle: {
                            color: splitLineColor
                        }
                    },
                    labelLayout: {
                        hideOverlap: true
                    },
                    data: this.currentData
                        .filter((d) => d.amount > 0 || d.count > 0 || d.percentage > 0)
                        .map((d) => ({
                            name: d.name,
                            value: Number(d.value),
                            percentage: d.percentage,
                            amount: d.amount,
                            count: d.count,
                            itemStyle: { color: d.color }
                        }))
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (symbol: string) => this.setupChart(symbol),
            (type: any, data: any) => this.emit(type, data)
        );
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseMarketInsiderAcquisitionDispositionDistribution: typeof TraderverseMarketInsiderAcquisitionDispositionDistribution; } }
if (typeof window !== 'undefined') window.TraderverseMarketInsiderAcquisitionDispositionDistribution = TraderverseMarketInsiderAcquisitionDispositionDistribution;
